// client/src/pages/Admin/ProjectManager/types/project.types.ts

/**
 * PROJECT MANAGER TYPES
 *
 * This file contains all TypeScript types, interfaces, and enums
 * used throughout the ProjectManager feature.
 *
 * @module ProjectManager/Types
 */

// ============================================
// CORE PROJECT TYPES (Re-exported from shared)
// ============================================

import {
    Project as BaseProject,
    ProjectCategory as SharedProjectCategory,
    ProjectStatus as SharedProjectStatus,
    ProjectImpact as SharedProjectImpact
} from '../../../../shared/models/Project';

/**
 * Project entity - core data structure
 * Extends the base Project from shared models
 */
export type Project = BaseProject;

/**
 * Project category - defines the type of project
 * @example 'education', 'health', 'governance'
 */
export type ProjectCategory = SharedProjectCategory;

/**
 * Project status - workflow state of the project
 * @example 'draft', 'published', 'archived', 'coming_soon'
 */
export type ProjectStatus = SharedProjectStatus;

/**
 * Project impact level - estimated impact of the project
 * @example 'low', 'medium', 'high', 'critical'
 */
export type ProjectImpact = SharedProjectImpact;

// ============================================
// ENUMS & CONSTANTS
// ============================================

/**
 * Project Category Labels - human-readable labels for UI
 */
export const ProjectCategoryLabels: Record<ProjectCategory, string> = {
    [SharedProjectCategory.GOVERNANCE]: 'Governance',
    [SharedProjectCategory.POLICY_ADVOCACY]: 'Policy Advocacy & Reform',
    [SharedProjectCategory.ENVIRONMENTAL]: 'Environmental Sustainability',
    [SharedProjectCategory.RESEARCH_DATA]: 'Research & Data Collection',
    [SharedProjectCategory.PARTNERSHIPS]: 'Partnership Building',
    [SharedProjectCategory.CULTURAL_SHIFT]: 'Cultural Paradigm Shift',
    [SharedProjectCategory.REMEMBRANCE]: 'Doctrine of Remembrance'
};

/**
 * Project Status Labels - human-readable labels for UI
 */
export const ProjectStatusLabels: Record<ProjectStatus, string> = {
    [SharedProjectStatus.DRAFT]: 'Draft',
    [SharedProjectStatus.PUBLISHED]: 'Published',
    [SharedProjectStatus.ARCHIVED]: 'Archived',
    [SharedProjectStatus.COMING_SOON]: 'Coming Soon'
};

/**
 * Project Impact Labels - human-readable labels for UI
 */
export const ProjectImpactLabels: Record<ProjectImpact, string> = {
    [SharedProjectImpact.LOW]: 'Low Impact',
    [SharedProjectImpact.MEDIUM]: 'Medium Impact',
    [SharedProjectImpact.HIGH]: 'High Impact',
    [SharedProjectImpact.CRITICAL]: 'Critical Impact'
};

/**
 * Project Impact Colors - color mapping for UI badges
 */
export const ProjectImpactColors: Record<ProjectImpact, string> = {
    [SharedProjectImpact.LOW]: 'impact-low',
    [SharedProjectImpact.MEDIUM]: 'impact-medium',
    [SharedProjectImpact.HIGH]: 'impact-high',
    [SharedProjectImpact.CRITICAL]: 'impact-critical'
};

// ============================================
// DTOs (Data Transfer Objects)
// ============================================

/**
 * Create Project DTO - used for creating new projects
 */
export interface CreateProjectDTO {
    title: string;
    subtitle?: string;
    category: ProjectCategory;
    description: string;
    fullDescription?: string;
    objectives: string[];
    approach?: string;
    expectedOutcomes: string[];
    impact: ProjectImpact;
    startDate: Date;
    endDate?: Date;
    isOngoing: boolean;
    provinces: string[];
    districts: string[];
    tags: string[];
    featured: boolean;
    featuredImage?: {
        url: string;
        alt: string;
        caption: string;
        isFeatured: boolean;
    };
    status: ProjectStatus;
}

/**
 * Update Project DTO - used for updating existing projects
 */
export interface UpdateProjectDTO extends Partial<CreateProjectDTO> {
    id: string;
}

/**
 * Project Filter Options - used for filtering projects
 */
export interface ProjectFilterOptions {
    status?: ProjectStatus | 'all';
    category?: ProjectCategory | 'all';
    impact?: ProjectImpact | 'all';
    searchTerm?: string;
    featured?: boolean | 'all';
    isOngoing?: boolean | 'all';
}

// ============================================
// UI STATE TYPES
// ============================================

/**
 * Project Manager State - main component state
 */
export interface ProjectManagerState {
    projects: Project[];
    loading: boolean;
    error: string | null;
    filters: ProjectFilterOptions;
    selectedIds: Set<string>;
    sortOption: string;
    sortDirection: 'asc' | 'desc';
    currentPage: number;
    pageSize: number;
    searchTerm: string;
    debouncedSearchTerm: string;
    showFilters: boolean;
    openActionMenu: string | null;
    editingProject: Project | null;
    showEditor: boolean;
    isBulkActionLoading: boolean;
}

/**
 * Project Stats - computed statistics
 */
export interface ProjectStats {
    all: number;
    draft: number;
    published: number;
    archived: number;
    coming_soon: number;
}

/**
 * Stat Card - for displaying statistics in the UI
 */
export interface StatCard {
    label: string;
    value: number;
    icon: React.ComponentType;
    color: string;
    bgColor: string;
}

// ============================================
// SEARCH & MATCH TYPES
// ============================================

/**
 * Search Match Result - for highlighting search matches
 */
export interface SearchMatchResult {
    matches: boolean;
    matchFields: string[];
}

/**
 * Search Suggestion - for autocomplete
 */
export interface SearchSuggestion {
    type: 'title' | 'category' | 'tag' | 'author' | 'region';
    value: string;
    label: string;
}

// ============================================
// PAGINATION TYPES
// ============================================

/**
 * Pagination State - for pagination hooks
 */
export interface PaginationState {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    startIndex: number;
    endIndex: number;
    pageNumbers: (number | string)[];
}

/**
 * Page Size Option - for pagination dropdown
 */
export interface PageSizeOption {
    value: number;
    label: string;
}

// ============================================
// BULK ACTION TYPES
// ============================================

/**
 * Bulk Action Types - for bulk operations
 */
export type BulkActionType =
    | 'publish'
    | 'archive'
    | 'feature'
    | 'unfeature'
    | 'delete'
    | 'export';

/**
 * Bulk Action State - for bulk action management
 */
export interface BulkActionState {
    selectedIds: Set<string>;
    isAllSelected: boolean;
    isLoading: boolean;
    actionType: BulkActionType | null;
}

// ============================================
// MODAL TYPES
// ============================================

/**
 * Modal Type - for confirmation modals
 */
export type ModalType = 'danger' | 'warning' | 'info' | 'success';

/**
 * Modal State - for managing modals
 */
export interface ModalState {
    isOpen: boolean;
    type: ModalType;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    isLoading: boolean;
}

// ============================================
// COMPONENT PROPS TYPES
// ============================================

/**
 * ProjectCard Props
 */
export interface ProjectCardProps {
    project: Project;
    isSelected: boolean;
    onToggleSelection: (id: string) => void;
    onEdit: (project: Project) => void;
    onDelete: (id: string) => void;
    onStatusChange: (id: string, status: ProjectStatus) => void;
    onToggleActionMenu: (id: string, e: React.MouseEvent) => void;
    isActionMenuOpen: boolean;
    matchFields: string[];
    debouncedSearchTerm: string;
}

/**
 * ProjectGrid Props
 */
export interface ProjectGridProps {
    projects: Project[];
    selectedIds: Set<string>;
    onToggleSelection: (id: string) => void;
    onEdit: (project: Project) => void;
    onDelete: (id: string) => void;
    onStatusChange: (id: string, status: ProjectStatus) => void;
    onToggleActionMenu: (id: string, e: React.MouseEvent) => void;
    openActionMenu: string | null;
    getMatchFields: (project: Project) => string[];
    debouncedSearchTerm: string;
}

/**
 * ProjectHeader Props
 */
export interface ProjectHeaderProps {
    onRefresh: () => void;
    onNewProject: () => void;
    projectsCount: number;
}

/**
 * ProjectFilters Props
 */
export interface ProjectFiltersProps {
    filters: ProjectFilterOptions;
    filterOptions: {
        categories: ProjectCategory[];
        statuses: ProjectStatus[];
        impacts: ProjectImpact[];
    };
    onFilterChange: (
        key: keyof ProjectFilterOptions,
        value: string | boolean
    ) => void;
    hasActiveFilters: boolean;
    onResetFilters: () => void;
}

/**
 * ProjectToolbar Props
 */
export interface ProjectToolbarProps {
    showFilters: boolean;
    onToggleFilters: () => void;
    activeFilterCount: number;
    hasActiveFilters: boolean;
    onResetFilters: () => void;
    sortOption: string;
    sortDirection: 'asc' | 'desc';
    onSortChange: (value: string) => void;
    onToggleSortDirection: () => void;
    searchTerm: string;
    onSearchChange: (value: string) => void;
    onClearSearch: () => void;
    isSearchFocused: boolean;
    onSearchFocus: () => void;
    onSearchBlur: () => void;
}

// ============================================
// HOOK RETURN TYPES
// ============================================

/**
 * useProjects Hook Return
 *
 * These signatures intentionally match the ProjectService
 * createProject/updateProject contracts.
 */
export interface UseProjectsReturn {
    projects: Project[];
    loading: boolean;
    error: string;
    loadProjects: () => Promise<void>;
    removeProject: (id: string) => Promise<void>;
    updateProjectStatus: (
        id: string,
        status: ProjectStatus
    ) => Promise<void>;
    addProject: (project: CreateProjectDTO) => Promise<Project>;
    updateProject: (
        id: string,
        updates: Partial<CreateProjectDTO>
    ) => Promise<Project>;
}

/**
 * useProjectFilters Hook Return
 */
export interface UseProjectFiltersReturn {
    filters: ProjectFilterOptions;
    filterOptions: {
        categories: ProjectCategory[];
        statuses: ProjectStatus[];
        impacts: ProjectImpact[];
    };
    filteredProjects: Project[];
    hasActiveFilters: boolean;
    activeFilterCount: number;
    updateFilter: (
        key: keyof ProjectFilterOptions,
        value: string | boolean
    ) => void;
    resetFilters: () => void;
}

// ============================================
// SERVICE TYPES
// ============================================

/**
 * Project Service Interface
 *
 * This interface matches the projectService implementation.
 */
export interface ProjectService {
    fetchProjects: () => Promise<Project[]>;
    getProject: (id: string) => Promise<Project>;
    createProject: (project: CreateProjectDTO) => Promise<Project>;
    updateProject: (
        id: string,
        updates: Partial<CreateProjectDTO>
    ) => Promise<Project>;
    deleteProject: (id: string) => Promise<void>;
    bulkUpdateProjects: (
        ids: string[],
        updates: Partial<Project>
    ) => Promise<void>;
    bulkDeleteProjects: (ids: string[]) => Promise<void>;
}